import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Table, Badge, Spinner, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaUserGraduate, FaEnvelope, FaPhone, FaCalendarAlt, FaUniversity, FaBookOpen, FaComments, FaEye, FaEdit, FaTrash, FaPlus, FaSearch, FaFilter, FaClock, FaCheck, FaTimes, FaSort, FaSortUp, FaSortDown, FaEraser } from 'react-icons/fa';
import { toast } from 'react-toastify';
// FIX: Corrected the import path. Please adjust './api/axios' if your file structure is different.
import axiosInstance from '../api/axios';

const STATUS_OPTIONS = [
    { value: 'NEW', label: 'New', color: 'primary' },
    { value: 'CONTACTED', label: 'Contacted', color: 'info' },
    { value: 'IN_PROGRESS', label: 'In Progress', color: 'warning' },
    { value: 'ADMITTED', label: 'Admitted', color: 'success' },
    { value: 'REJECTED', label: 'Rejected', color: 'danger' },
    { value: 'CLOSED', label: 'Closed', color: 'secondary' }
];

const BRANCH_OPTIONS = [
    'Computer Science',
    'Mechanical Engineering', 
    'Civil Engineering',
    'Electrical Engineering',
    'Electronics Engineering',
    'Chemical Engineering',
    'Aerospace Engineering',
    'Biomedical Engineering',
    'Artificial Intelligence',
    'Data Science',
    'Robotics',
    'Biotechnology'
];

const StudentEnquiries = () => {
    const [allEnquiries, setAllEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBranch, setFilterBranch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        fullName: '', dob: '', email: '', phone: '', collegeName: '',
        branch: '', choices: [], enquiryStatus: 'NEW', remarks: ''
    });
    const [formErrors, setFormErrors] = useState({});

    const fetchEnquiries = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/academic/student-enquiries');
            setAllEnquiries(response.data.data || response.data || []);
        } catch (error) {
            console.error('Error fetching enquiries:', error);
            if (!error.response) {
                toast.error('Network error. Please check your connection.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const displayedEnquiries = useMemo(() => {
        let filteredData = [...allEnquiries];

        if (searchTerm) {
            filteredData = filteredData.filter(e => e.fullName.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        if (filterBranch) {
            filteredData = filteredData.filter(e => e.branch === filterBranch);
        }
        if (filterStatus) {
            filteredData = filteredData.filter(e => e.enquiryStatus === filterStatus);
        }

        return filteredData.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            
            if (sortField === 'createdAt' || sortField === 'updatedAt') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }
            
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [allEnquiries, searchTerm, filterBranch, filterStatus, sortField, sortDirection]);

    const updateStatus = async (id, status) => {
        try {
            await axiosInstance.patch(`/academic/student-enquiry/${id}/status?status=${status}`);
            toast.success('Status updated successfully');
            fetchEnquiries(); // Refetch data after update
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const deleteEnquiry = async (id) => {
        if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
        
        try {
            await axiosInstance.delete(`/academic/student-enquiry/${id}`);
            toast.success('Enquiry deleted successfully');
            fetchEnquiries(); // Refetch data after delete
        } catch (error) {
            console.error('Error deleting enquiry:', error);
            toast.error('Failed to delete enquiry');
        }
    };
    
    const validateForm = () => {
        const errors = {};
        if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
        if (!formData.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
        if (!formData.phone.trim()) errors.phone = 'Phone is required';
        else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ''))) errors.phone = 'Phone must be 10 digits';
        if (!formData.dob) errors.dob = 'Date of birth is required';
        if (!formData.collegeName.trim()) errors.collegeName = 'College name is required';
        if (!formData.branch && (!formData.choices || formData.choices.length === 0)) {
            errors.choices = 'At least one choice or a primary branch is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setSubmitting(true);
        
        const payload = {
            fullName: formData.fullName.trim(),
            dob: formData.dob,
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            collegeName: formData.collegeName.trim(),
            choices: formData.choices,
            branch: formData.branch || (formData.choices && formData.choices[0]) || null, // Set primary branch
            enquiryStatus: formData.enquiryStatus,
            remarks: formData.remarks.trim()
        };

        try {
            if (modalMode === 'create') {
                await axiosInstance.post('/academic/student-enquiry', payload);
                toast.success('Enquiry created successfully');
            } else {
                await axiosInstance.put(`/academic/student-enquiry/${selectedEnquiry.id}`, payload);
                toast.success('Enquiry updated successfully');
            }
            
            setShowModal(false);
            resetForm();
            fetchEnquiries(); // Refetch data after create/update
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error(error.response?.data?.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} enquiry`);
        } finally {
            setSubmitting(false);
        }
    };
    
    const resetForm = () => {
        setFormData({
            fullName: '', dob: '', email: '', phone: '', collegeName: '',
            branch: '', choices: [], enquiryStatus: 'NEW', remarks: ''
        });
        setFormErrors({});
        setSelectedEnquiry(null);
    };

    const openModal = (mode, enquiry = null) => {
        setModalMode(mode);
        if (mode === 'edit' && enquiry) {
            setSelectedEnquiry(enquiry);
            setFormData({
                fullName: enquiry.fullName || '',
                dob: enquiry.dob || '',
                email: enquiry.email || '',
                phone: enquiry.phone || '',
                collegeName: enquiry.collegeName || '',
                branch: enquiry.branch || '',
                choices: enquiry.choices || [],
                enquiryStatus: enquiry.enquiryStatus || 'NEW',
                remarks: enquiry.remarks || ''
            });
        } else {
            resetForm();
        }
        setShowModal(true);
    };

    const viewEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setShowViewModal(true);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <FaSort className="text-muted opacity-50" />;
        return sortDirection === 'asc' ? <FaSortUp className="text-primary" /> : <FaSortDown className="text-primary" />;
    };

    const getStatusBadge = (status) => {
        const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || { value: status, label: status, color: 'secondary' };
        return (
            <Badge bg={`${statusConfig.color}-subtle`} text={`${statusConfig.color}-emphasis`} className="px-2 py-1">
                {statusConfig.label}
            </Badge>
        );
    };
    
    const handleChoiceChange = (choice) => {
        setFormData(prev => {
            const newChoices = prev.choices.includes(choice)
                ? prev.choices.filter(c => c !== choice)
                : [...prev.choices, choice];
            return { ...prev, choices: newChoices };
        });
        if (formErrors.choices) {
            setFormErrors(prev => ({ ...prev, choices: '' }));
        }
    };
    
    const clearFilters = () => {
        setSearchTerm('');
        setFilterBranch('');
        setFilterStatus('');
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-0 d-flex align-items-center">
                                <FaUserGraduate className="me-2 text-primary" />
                                Student Enquiries
                            </h2>
                            <p className="text-muted mb-0">Manage student admission enquiries</p>
                        </div>
                        <Button variant="primary" onClick={() => openModal('create')}>
                            <FaPlus className="me-2" />
                            Add New Enquiry
                        </Button>
                    </div>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={5}>
                            <InputGroup>
                                <InputGroup.Text><FaSearch /></InputGroup.Text>
                                <Form.Control
                                    placeholder="Search by name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
                                <option value="">All Branches</option>
                                {BRANCH_OPTIONS.map(branch => (
                                    <option key={branch} value={branch}>{branch}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                                <option value="">All Status</option>
                                {STATUS_OPTIONS.map(status => (
                                    <option key={status.value} value={status.value}>{status.label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={1}>
                            <Button variant="outline-secondary" onClick={clearFilters} className="w-100">
                                <FaEraser />
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="mt-2">Loading enquiries...</p>
                        </div>
                    ) : (
                        <Table responsive hover className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="py-3 px-3 cursor-pointer" onClick={() => handleSort('fullName')}>
                                        Student Details {getSortIcon('fullName')}
                                    </th>
                                    <th className="py-3 px-3 cursor-pointer" onClick={() => handleSort('collegeName')}>
                                        College {getSortIcon('collegeName')}
                                    </th>
                                    <th className="py-3 px-3 cursor-pointer" onClick={() => handleSort('enquiryStatus')}>
                                        Status {getSortIcon('enquiryStatus')}
                                    </th>
                                    <th className="py-3 px-3 cursor-pointer" onClick={() => handleSort('createdAt')}>
                                        Enquiry Date {getSortIcon('createdAt')}
                                    </th>
                                    <th className="py-3 px-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedEnquiries.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-5 text-muted">
                                            <FaUserGraduate size={48} className="mb-3 opacity-25" />
                                            <h4>No Enquiries Found</h4>
                                            <p>Try adjusting your search or filters.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    displayedEnquiries.map((enquiry) => (
                                        <tr key={enquiry.id}>
                                            <td className="px-3">
                                                <div>
                                                    <div className="fw-bold">{enquiry.fullName}</div>
                                                    <div className="text-muted small">
                                                        <FaEnvelope className="me-1" />{enquiry.email}
                                                    </div>
                                                    <div className="text-muted small">
                                                        <FaPhone className="me-1" />{enquiry.phone}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3">
                                                <div>
                                                    <div className="fw-medium">{enquiry.collegeName}</div>
                                                    {enquiry.branch && (
                                                        <Badge bg="info-subtle" text="info-emphasis" className="small">
                                                            {enquiry.branch}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3" style={{ minWidth: '170px' }}>
                                                <Form.Select
                                                    size="sm"
                                                    value={enquiry.enquiryStatus}
                                                    onChange={(e) => updateStatus(enquiry.id, e.target.value)}
                                                >
                                                    {STATUS_OPTIONS.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </Form.Select>
                                            </td>
                                            <td className="px-3">
                                                <div className="small">
                                                    <div>{new Date(enquiry.createdAt).toLocaleDateString()}</div>
                                                    <div className="text-muted">{new Date(enquiry.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>View</Tooltip>}>
                                                        <Button variant="outline-info" size="sm" onClick={() => viewEnquiry(enquiry)}>
                                                            <FaEye />
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                        <Button variant="outline-primary" size="sm" onClick={() => openModal('edit', enquiry)}>
                                                            <FaEdit />
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                                                        <Button variant="outline-danger" size="sm" onClick={() => deleteEnquiry(enquiry.id)}>
                                                            <FaTrash />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FaUserGraduate className="me-2" />
                            {modalMode === 'create' ? 'Add New Enquiry' : 'Edit Enquiry'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                                        isInvalid={!!formErrors.fullName}
                                    />
                                    <Form.Control.Feedback type="invalid">{formErrors.fullName}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.dob}
                                        onChange={(e) => setFormData({...formData, dob: e.target.value})}
                                        isInvalid={!!formErrors.dob}
                                    />
                                    <Form.Control.Feedback type="invalid">{formErrors.dob}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        isInvalid={!!formErrors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                                        isInvalid={!!formErrors.phone}
                                    />
                                    <Form.Control.Feedback type="invalid">{formErrors.phone}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>College Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.collegeName}
                                onChange={(e) => setFormData({...formData, collegeName: e.target.value})}
                                isInvalid={!!formErrors.collegeName}
                            />
                            <Form.Control.Feedback type="invalid">{formErrors.collegeName}</Form.Control.Feedback>
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Preferred Branch</Form.Label>
                                    <Form.Select
                                        value={formData.branch}
                                        onChange={(e) => setFormData({...formData, branch: e.target.value})}
                                    >
                                        <option value="">Select Branch</option>
                                        {BRANCH_OPTIONS.map(branch => (
                                            <option key={branch} value={branch}>{branch}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={formData.enquiryStatus}
                                        onChange={(e) => setFormData({...formData, enquiryStatus: e.target.value})}
                                    >
                                        {STATUS_OPTIONS.map(status => (
                                            <option key={status.value} value={status.value}>{status.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Course Choices <span className="text-danger">*</span></Form.Label>
                            <div className="border rounded p-3" style={{maxHeight: '200px', overflowY: 'auto'}}>
                                <Row>
                                    {BRANCH_OPTIONS.map(choice => (
                                        <Col md={6} key={choice} className="mb-2">
                                            <Form.Check
                                                type="checkbox"
                                                id={`choice-${choice}`}
                                                label={choice}
                                                checked={formData.choices.includes(choice)}
                                                onChange={() => handleChoiceChange(choice)}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                            {formErrors.choices && <div className="text-danger small mt-1">{formErrors.choices}</div>}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Remarks</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.remarks}
                                onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                                placeholder="Additional notes..."
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    {modalMode === 'create' ? 'Creating...' : 'Updating...'}
                                </>
                            ) : (
                                modalMode === 'create' ? 'Create Enquiry' : 'Update Enquiry'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* View Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaEye className="me-2" />
                        Enquiry Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedEnquiry && (
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaUserGraduate className="me-2" />Full Name
                                    </strong>
                                    <p className="mb-0">{selectedEnquiry.fullName}</p>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaEnvelope className="me-2" />Email
                                    </strong>
                                    <p className="mb-0">{selectedEnquiry.email}</p>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaPhone className="me-2" />Phone
                                    </strong>
                                    <p className="mb-0">{selectedEnquiry.phone}</p>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaCalendarAlt className="me-2" />Date of Birth
                                    </strong>
                                    <p className="mb-0">{new Date(selectedEnquiry.dob).toLocaleDateString()}</p>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaUniversity className="me-2" />College
                                    </strong>
                                    <p className="mb-0">{selectedEnquiry.collegeName}</p>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaBookOpen className="me-2" />Branch
                                    </strong>
                                    <p className="mb-0">{selectedEnquiry.branch || 'Not specified'}</p>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaClock className="me-2" />Status
                                    </strong>
                                    <p className="mb-0">{getStatusBadge(selectedEnquiry.enquiryStatus)}</p>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaCalendarAlt className="me-2" />Created
                                    </strong>
                                    <p className="mb-0">{new Date(selectedEnquiry.createdAt).toLocaleString()}</p>
                                </div>
                            </Col>
                            <Col xs={12}>
                                <div className="mb-3">
                                    <strong className="d-flex align-items-center">
                                        <FaBookOpen className="me-2" />Course Choices
                                    </strong>
                                    <p className="mb-0">{selectedEnquiry.choices?.join(', ') || 'None specified'}</p>
                                </div>
                                {selectedEnquiry.remarks && (
                                    <div className="mb-3">
                                        <strong className="d-flex align-items-center">
                                            <FaComments className="me-2" />Remarks
                                        </strong>
                                        <p className="mb-0">{selectedEnquiry.remarks}</p>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                    {selectedEnquiry && (
                        <Button variant="primary" onClick={() => {
                            setShowViewModal(false);
                            openModal('edit', selectedEnquiry);
                        }}>
                            <FaEdit className="me-2" />
                            Edit Enquiry
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .card-hover-effect { transition: transform 0.2s ease; }
                .card-hover-effect:hover { transform: translateY(-2px); }
            `}</style>
        </Container>
    );
};

export default StudentEnquiries;

